import { z } from 'zod';

import { AudioRecordingPartialResult } from '../../main/types/audio.types.ts';

import {
  SpeechSuperCharTypeEnum,
  SpeechSuperReadTypeEnum,
  SpeechSuperStressEnum,
} from '../constants/speechsuper.constants.ts';

import { SpeechSuperStartAppParamsSchema } from './pronunciation-grading-initialization.types.ts';
import {
  SpeechSuperAudioParamsSchema,
  SpeechSuperSentenceRequestParamsSchema,
  SpeechSuperWordRequestParamsSchema,
} from './speechsuper-request.types.ts';

// Response Schemas
export const SpeechSuperSpanSchema = z.object({
  start: z.number(),
  end: z.number(),
});

export const SpeechSuperPhonemeSchema = z.object({
  pronunciation: z.number(),
  span: SpeechSuperSpanSchema,
  phoneme: z.string(),
  readType: z.nativeEnum(SpeechSuperReadTypeEnum).transform(Number),
  sound_like: z.string(),
  inserted_before: z.array(z.string()),
  inserted_after: z.array(z.string()),
});

export const SpeechSuperPhonicsSchema = z.object({
  overall: z.number(),
  phoneme: z.array(z.string()),
  spell: z.string(),
});

export const SpeechSuperStressSchema = z.object({
  overall: z.number(),
  phonetic: z.string(),
  spell: z.string(),
  stress: z.nativeEnum(SpeechSuperStressEnum).transform(Number),
  ref_stress: z.nativeEnum(SpeechSuperStressEnum).transform(Number),
  phoneme_offset: z.number(),
});

export const SpeechSuperWordScoresSchema = z.object({
  overall: z.number(),
  stress: z.array(SpeechSuperStressSchema),
  pronunciation: z.number(),
});

export const SpeechSuperWordResultSchema = z.object({
  charType: z.nativeEnum(SpeechSuperCharTypeEnum).transform(Number),
  word: z.string(),
  scores: SpeechSuperWordScoresSchema,
  pause: z.object({
    duration: z.number(),
    type: z.number(),
  }),
  phonemes: z.array(SpeechSuperPhonemeSchema),
  span: SpeechSuperSpanSchema,
  phonics: z.array(SpeechSuperPhonicsSchema),
  word_parts: z.array(
    z.object({
      part: z.string(),
      charType: z.nativeEnum(SpeechSuperCharTypeEnum).transform(Number),
      beginIndex: z.number(),
      endIndex: z.number(),
    })
  ),
  readType: z.nativeEnum(SpeechSuperReadTypeEnum).transform(Number).optional(),
  repetition: z
    .array(
      z.object({
        start: z.number(),
        end: z.number(),
        overall: z.number(),
      })
    )
    .optional(),
});

export const SpeechSuperWarningSchema = z.object({
  code: z.number(),
  message: z.string(),
});

export const SpeechSuperSentenceWordResultSchema = z.object({
  word: z.string(),
  scores: z.object({
    overall: z.number(),
    pronunciation: z.number(),
    fluency: z.number().optional(),
    integrity: z.number().optional(),
  }),
  span: SpeechSuperSpanSchema,
  readType: z.nativeEnum(SpeechSuperReadTypeEnum).transform(Number).optional(),
});

export const SpeechSuperSentenceAssessmentResultSchema = z.object({
  resource_version: z.string(),
  words: z.array(SpeechSuperSentenceWordResultSchema),
  duration: z.string(),
  overall: z.number(),
  pronunciation: z.number(),
  fluency: z.number(),
  integrity: z.number(),
  speed: z.number(),
  kernel_version: z.string(),
  warning: z.array(SpeechSuperWarningSchema).optional(),
});

export const SpeechSuperSentenceAssessmentIntermediateResultSchema = z.object({
  overall: z.number(),
  pronunciation: z.number(),
  kernel_version: z.string(),
  resource_version: z.string(),
  words: z.array(
    z.object({
      scores: z.object({
        pronunciation: z.number(),
      }),
      charType: z.number(),
      word: z.string(),
      word_parts: z.array(
        z.object({
          part: z.string(),
          charType: z.number(),
        })
      ),
    })
  ),
});

export const SpeechSuperWordAssessmentResultSchema = z.object({
  resource_version: z.string(),
  words: z.array(SpeechSuperWordResultSchema),
  duration: z.string(),
  overall: z.number(),
  stress: z.number(),
  kernel_version: z.string(),
  pause_count: z.number(),
  pronunciation: z.number(),
  warning: z.array(SpeechSuperWarningSchema).optional(),
});

export const SpeechSuperAssessmentIntermediateResponseSchema = z.object({
  eof: z.literal(0),
  result: SpeechSuperSentenceAssessmentIntermediateResultSchema,
  timestamp: z.string(),
  recordId: z.string(),
});

const SpeechSuperAssessmentFinalResponseBase = z.object({
  tokenId: z.string(),
  dtLastResponse: z.string(),
  applicationId: z.string(),
  recordId: z.string(),
  refText: z.string(),
  eof: z.literal(1),
});

export const SpeechSuperAssessmentFinalResponseSchema = z.union([
  SpeechSuperAssessmentFinalResponseBase.extend({
    result: SpeechSuperWordAssessmentResultSchema,
    params: z.object({
      app: SpeechSuperStartAppParamsSchema,
      request: SpeechSuperWordRequestParamsSchema,
      audio: SpeechSuperAudioParamsSchema,
    }),
  }),
  SpeechSuperAssessmentFinalResponseBase.extend({
    result: SpeechSuperSentenceAssessmentResultSchema,
    params: z.object({
      app: SpeechSuperStartAppParamsSchema,
      request: SpeechSuperSentenceRequestParamsSchema,
      audio: SpeechSuperAudioParamsSchema,
    }),
  }),
]);

export const SpeechSuperAssessmentResponseSchema = z.union([
  SpeechSuperAssessmentIntermediateResponseSchema,
  SpeechSuperAssessmentFinalResponseSchema,
]);

// Type Inference
export type SpeechSuperSpan = z.infer<typeof SpeechSuperSpanSchema>;
export type SpeechSuperPhoneme = z.infer<typeof SpeechSuperPhonemeSchema>;
export type SpeechSuperPhonics = z.infer<typeof SpeechSuperPhonicsSchema>;
export type SpeechSuperStress = z.infer<typeof SpeechSuperStressSchema>;
export type SpeechSuperWordScores = z.infer<typeof SpeechSuperWordScoresSchema>;
export type SpeechSuperWordResult = z.infer<typeof SpeechSuperWordResultSchema>;
export type SpeechSuperWarning = z.infer<typeof SpeechSuperWarningSchema>;
export type SpeechSuperFinalAssessmentResponse = z.infer<typeof SpeechSuperAssessmentFinalResponseSchema>;
export type SpeechSuperIntermediateAssessmentResponse = z.infer<typeof SpeechSuperAssessmentIntermediateResponseSchema>;
export type SpeechSuperAudioMessage = AudioRecordingPartialResult;
export type SpeechSuperSentenceWordResult = z.infer<typeof SpeechSuperSentenceWordResultSchema>;
export type SpeechSuperSentenceAssessmentResult = z.infer<typeof SpeechSuperSentenceAssessmentResultSchema>;
export type SpeechSuperWordAssessmentResult = z.infer<typeof SpeechSuperWordAssessmentResultSchema>;
